import { z } from 'zod';
import { MoneyCentsSchema } from './money.js';
export const DiscountRuleKindSchema = z.enum(['percent', 'amount']);
export const DiscountTargetTypeSchema = z.enum(['product', 'category']);
export const DiscountRuleStatusSchema = z.enum(['active', 'scheduled', 'expired', 'off']);
export const DiscountRuleLineSchema = z.object({
    id: z.number().int(),
    targetType: DiscountTargetTypeSchema,
    productId: z.number().int().nullable(),
    productName: z.string().nullable(),
    categoryId: z.number().int().nullable(),
    categoryName: z.string().nullable(),
    minQty: z.number().int().min(1),
});
export const DiscountRuleSchema = z.object({
    id: z.number().int(),
    name: z.string(),
    kind: DiscountRuleKindSchema,
    /** Basis points, 1000 = 10%. Set for `percent`, null for `amount`. */
    percentBp: z.number().int().nullable(),
    /** Set for `amount`, null for `percent`. */
    amountCents: MoneyCentsSchema.nullable(),
    priority: z.number().int(),
    isActive: z.boolean(),
    startsAt: z.string().nullable(),
    endsAt: z.string().nullable(),
    status: DiscountRuleStatusSchema,
    lines: z.array(DiscountRuleLineSchema),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const DiscountRuleListResponseSchema = z.object({
    rules: z.array(DiscountRuleSchema),
});
export const CreateDiscountRuleLineRequestSchema = z
    .object({
    targetType: DiscountTargetTypeSchema,
    productId: z.number().int().positive().nullable().optional(),
    categoryId: z.number().int().positive().nullable().optional(),
    minQty: z.number().int().min(1).max(10_000),
})
    .refine((v) => (v.targetType === 'product' && v.productId != null && v.categoryId == null) ||
    (v.targetType === 'category' && v.categoryId != null && v.productId == null), { message: 'productId must be set for a product line, categoryId for a category line (not both)' });
const BaseDiscountRuleRequestSchema = z.object({
    name: z.string().trim().min(1).max(80),
    kind: DiscountRuleKindSchema,
    /** Whole percent 1..100 from the UI; stored as basis points (x100). */
    percent: z.number().int().min(1).max(100).optional(),
    amountCents: z.number().int().positive().optional(),
    lines: z.array(CreateDiscountRuleLineRequestSchema).min(1).max(20),
    isActive: z.boolean().default(true),
    startsAt: z.string().datetime().nullable().optional(),
    endsAt: z.string().datetime().nullable().optional(),
});
export const CreateDiscountRuleRequestSchema = BaseDiscountRuleRequestSchema.refine((v) => (v.kind === 'percent' ? v.percent !== undefined : v.amountCents !== undefined), { message: 'percent is required for a percent rule, amountCents for an amount rule' }).refine((v) => !v.startsAt || !v.endsAt || v.endsAt > v.startsAt, {
    message: 'endsAt must be after startsAt',
    path: ['endsAt'],
});
export const UpdateDiscountRuleRequestSchema = z
    .object({
    name: z.string().trim().min(1).max(80).optional(),
    kind: DiscountRuleKindSchema.optional(),
    percent: z.number().int().min(1).max(100).optional(),
    amountCents: z.number().int().positive().optional(),
    lines: z.array(CreateDiscountRuleLineRequestSchema).min(1).max(20).optional(),
    isActive: z.boolean().optional(),
    startsAt: z.string().datetime().nullable().optional(),
    endsAt: z.string().datetime().nullable().optional(),
})
    .refine((v) => !v.startsAt || !v.endsAt || v.endsAt > v.startsAt, {
    message: 'endsAt must be after startsAt',
    path: ['endsAt'],
});
export const ReorderDiscountRulesRequestSchema = z.object({
    ids: z.array(z.number().int().positive()).min(1),
});
/** Snapshot of the winning automatic discount, shared by the preview endpoint and sale responses. */
export const AutoDiscountSchema = z.object({
    ruleId: z.number().int(),
    label: z.string(),
    cents: MoneyCentsSchema,
});
export const PreviewSaleResponseSchema = z.object({
    subtotalCents: MoneyCentsSchema,
    autoDiscount: AutoDiscountSchema.nullable(),
    maxManualDiscountCents: MoneyCentsSchema,
    totalCents: MoneyCentsSchema,
});
//# sourceMappingURL=discounts.js.map